import React, { useContext, useMemo } from 'react';
import { Platform, StyleSheet, View } from 'react-native';

import { adUnits } from '../constants';
import JobsContext from '../context/JobsContext';
import { track } from '../lib/analytics';
import { Palette, useThemePalette } from '../theme';

// Lazy-loaded native banner component. Mirrors analytics.ts: `require`
// guarded by Platform check so the web bundle never resolves the native
// ads SDK (which would crash on import).
type BannerAdComp = React.ComponentType<{
  unitId: string;
  size: string;
  onAdLoaded?: () => void;
  onAdFailedToLoad?: (error: Error) => void;
  onAdImpression?: () => void;
  onAdClicked?: () => void;
}>;

let BannerAd: BannerAdComp | null = null;
let bannerSize = 'ANCHORED_ADAPTIVE_BANNER';

if (Platform.OS !== 'web') {
  try {
    // eslint-disable-next-line @typescript-eslint/no-require-imports
    const ads = require('react-native-google-mobile-ads');
    BannerAd = ads.BannerAd;
    bannerSize = ads.BannerAdSize.ANCHORED_ADAPTIVE_BANNER;
  } catch (err) {
    if (__DEV__) console.warn('[ads] BannerAd unavailable:', err);
  }
}

const AdBanner: React.FC = () => {
  const palette = useThemePalette();
  const { hideAds, removeAdsEntitled } = useContext(JobsContext);
  const styles = useMemo(() => makeStyles(palette), [palette]);

  if (hideAds || removeAdsEntitled || !BannerAd) return null;

  return (
    <View style={styles.container}>
      <BannerAd
        unitId={adUnits.banner}
        size={bannerSize}
        onAdImpression={() => track('ad_impression', { format: 'banner' })}
        onAdClicked={() => track('cliked_on_ad', { format: 'banner' })}
        onAdFailedToLoad={(error) =>
          track('ad_load_failed', { format: 'banner', error: error.message })
        }
      />
    </View>
  );
};

const makeStyles = (p: Palette) =>
  StyleSheet.create({
    container: {
      backgroundColor: p.canvas,
      alignItems: 'center',
    },
  });

export default AdBanner;
